import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';

import Error from '../../../components/States/Error';
import NoResults from '../../../components/States/NoResults';
import Table from '../../../components/Table';
import ListItem from './ListItem';
import { useUserStatusContext } from '../../../context/UserStatus';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import MembersSparklingIllustration from '@hero/ui-kit/graphics/Illustrations/MembersSparklingIllustration';
import { CheckinMembersResponse } from '../types';
import useListQueryParams from '../OrganizationMemberList/useListQueryParams';
import { memberSortDefaultDir } from '../OrganizationMemberList/utils/memberSort';
import buildHeaderColumns from '../../../components/SortBy/buildHeaderColumns';
import { GetCheckinMembersParams } from '../api/useGetCheckinMembers';
import { buildGridTemplate } from '../../../utils/tableColumns';
import { CHECKIN_COLUMNS, CheckinColumnId } from './columns';
import { MISMATCH_ALERT_TRACK } from '../components/MemberMismatchAlert';

const CheckinMembersList: React.FC<{
    isLoading: boolean;
    isError: boolean;
    data?: CheckinMembersResponse;
    listQuery: GetCheckinMembersParams;
    isColumnVisible: (id: CheckinColumnId) => boolean;
}> = ({ isLoading, isError, data, listQuery, isColumnVisible }) => {
    const isMinL = useIsMinBreakpoint('l');
    const { permissions } = useUserStatusContext();

    const { query } = useListQueryParams();
    const search = query?.search || '';
    const hasFilters = !!query.device_status || !!query.is_online;

    const gridTemplate = buildGridTemplate(CHECKIN_COLUMNS, isColumnVisible, {
        trailingTracks: [MISMATCH_ALERT_TRACK],
        collapsed: !isMinL
    });
    const headerColumns = buildHeaderColumns(
        CHECKIN_COLUMNS,
        isColumnVisible,
        memberSortDefaultDir
    );

    if (isError) {
        return <Error />;
    }

    return (
        <>
            {data?.members.length === 0 && search.length === 0 && !hasFilters ? (
                <Section centered>
                    <H role="h6" styles={{ fontSize: '24px' }}>
                        No checkin members
                    </H>
                    <MembersSparklingIllustration
                        styles={{ height: '20rem', width: '64rem', marginTop: '5rem' }}
                    />
                </Section>
            ) : null}

            {data?.members.length === 0 && search.length ? (
                <NoResults search={search} searchSupportsLabel={'Name or E-mail'} />
            ) : null}

            {data?.members.length ? (
                <Table
                    pagerData={data}
                    headerColumns={headerColumns}
                    headerGridTemplateColumns={gridTemplate}
                    loading={isLoading}
                    gridColumnGap="small"
                >
                    {data?.members.map((item, index) => (
                        <ListItem
                            memberId={item.member.id}
                            heroUserId={item.member.hero_user_id}
                            key={`${index}-${item.member.hero_user_id || item.member.id}-${
                                item.member.email || 'n/a'
                            }`}
                            name={
                                item.member.first_name || item.member.last_name
                                    ? `${item.member.first_name} ${item.member.last_name}`
                                    : 'N/A'
                            }
                            firstName={item.member.first_name}
                            lastName={item.member.last_name}
                            dateOfBirth={item.member.date_of_birth}
                            deviceFirstName={item.member.fre_first_name}
                            deviceLastName={item.member.fre_last_name}
                            deviceDateOfBirth={item.member.fre_dob}
                            email={item.member.email || undefined}
                            search={search}
                            canOpenDetails={permissions.fleet.fleetMemberView}
                            activatedAt={item.member.activated_at}
                            externalUserId={item.member.external_user_id}
                            careManagerIds={item.member.care_manager_ids || null}
                            status={item.care_manager_checkin.status}
                            nextCheckinDate={item.care_manager_checkin.next_checkin_time}
                            isAutoScheduled={item.care_manager_checkin.is_auto_scheduled}
                            scheduledCheckinDate={item.care_manager_checkin.scheduled_check_in_date}
                            notificationPhase={item.care_manager_checkin.notification_phase}
                            listQuery={listQuery}
                            isColumnVisible={isColumnVisible}
                            gridColumns={gridTemplate}
                        />
                    ))}
                </Table>
            ) : null}
        </>
    );
};

export default CheckinMembersList;
